import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { forwardRef, type KeyboardEvent } from "react";

import { renderErrorOrLoading } from "../commons/renderErrorOrLoading";
import { OddsChip } from "../components/OddsChip";
import type {
  CalculatedResultRow,
  MatchupRow,
  ResultValue,
} from "../data/types";
import { useResults } from "../data/useResults";
import { useUser } from "../data/useUser";

interface ResultsDialogProps {
  matchup?: MatchupRow;
  onClose: () => void;
}

const CURRENT_USER_CELL_STYLE = { fontWeight: 700 };

export const ResultsDialog = forwardRef<HTMLDivElement, ResultsDialogProps>(
  function ResultsDialog({ matchup, onClose }, ref): JSX.Element | null {
    const { results, loading, error } = useResults(matchup?.index);
    const { user } = useUser();
    const errorOrLoading = renderErrorOrLoading({ loading, error });

    if (!matchup) return null;
    if (errorOrLoading) return errorOrLoading;

    return (
      <Box
        alignItems="center"
        data-testid="results-dialog-close-surface"
        display="flex"
        justifyContent="center"
        minHeight="100vh"
        onClick={onClose}
        onKeyDown={(event) => closeOnActivation(event, onClose)}
        ref={ref}
        role="dialog"
        aria-label="Matchup results"
        aria-modal="true"
        tabIndex={0}
        sx={{ cursor: "pointer", px: 2 }}
      >
        <TableContainer
          component={Paper}
          sx={{ maxHeight: 420, maxWidth: 420, overflow: "auto" }}
        >
          <Table aria-label="selected matchup results table">
            <TableHead>
              <TableRow>
                <TableCell>name</TableCell>
                <TableCell>pick</TableCell>
                <TableCell>total points</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((row, index) => (
                <ResultRow
                  key={`${row.name ?? "row"}-${index}`}
                  isCurrentUser={user === row.name}
                  result={row}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  },
);

interface ResultRowProps {
  isCurrentUser: boolean;
  result: CalculatedResultRow;
}

function ResultRow({ isCurrentUser, result }: ResultRowProps): JSX.Element {
  return (
    <TableRow>
      <TableCell
        component="th"
        scope="row"
        sx={isCurrentUser ? CURRENT_USER_CELL_STYLE : undefined}
      >
        {result.name}
      </TableCell>
      <TableCell>
        <OddsChip
          correct={result.correct}
          margin={0}
          mine={isCurrentUser}
          odds={result.curPotentialScore}
          sign={(result.curRow ?? "") as ResultValue}
        />
      </TableCell>
      <TableCell>{result.score}</TableCell>
    </TableRow>
  );
}

function closeOnActivation(
  event: KeyboardEvent<HTMLElement>,
  onClose: () => void,
): void {
  if (event.key !== "Enter" && event.key !== " ") return;

  event.preventDefault();
  onClose();
}
